import { MEMBER_TYPE } from "@/constants";
import {
  ChargeType,
  ClassScheduleByUserType,
  ClassScheduleDetailResponse,
} from "@/models";

const MEMBER_TYPE_TO_BUCKET_KEY: Record<string, keyof ClassScheduleByUserType> =
  {
    [MEMBER_TYPE.MEMBER]: "MEMBER",
    [MEMBER_TYPE.SESSION_MEMBER]: "SESSION",
    [MEMBER_TYPE.DROP_IN]: "DROP_IN",
  };

export const getScheduleByUserTypeKey = (
  memberType: string | null | undefined,
): keyof ClassScheduleByUserType | undefined =>
  memberType ? MEMBER_TYPE_TO_BUCKET_KEY[memberType] : undefined;

export const buildScheduleChargeLabel = (
  chargeType: ChargeType | undefined,
  price: number,
  isCreditEnabled: boolean,
): string => {
  if (chargeType === ChargeType.USE_SESSION) {
    return "Use Session";
  }
  if (chargeType === ChargeType.CREDIT) {
    return isCreditEnabled
      ? `${price.toLocaleString("en-US")} points`
      : "Complimentary";
  }
  if (chargeType === ChargeType.FREE) {
    return "Complimentary";
  }
  // Legacy data without chargeType: infer from price.
  if (isCreditEnabled && price > 0) {
    return `${price.toLocaleString("en-US")} points`;
  }
  return "Complimentary";
};

/**
 * Schedule-detail price text. Returns "" while the point-enabled flag is still
 * loading and the row would otherwise display a point amount — prevents a
 * flash of the wrong label.
 */
export const computeSchedulePriceText = (
  scheduleDetail: ClassScheduleDetailResponse | undefined,
  memberType: string | null | undefined,
  isCreditEnabled: boolean,
  isCreditConfigReady: boolean,
): string => {
  if (!scheduleDetail) return "";
  const byUserTypeKey = getScheduleByUserTypeKey(memberType);
  const byUserType = byUserTypeKey
    ? scheduleDetail.byUserType?.[byUserTypeKey]
    : undefined;
  const schedulePrice = byUserType?.price ?? scheduleDetail.price ?? 0;
  const chargeType = byUserType?.chargeType;

  const needsCreditConfig =
    chargeType === ChargeType.CREDIT ||
    (!chargeType && schedulePrice > 0);
  if (needsCreditConfig && !isCreditConfigReady) {
    return "";
  }
  return buildScheduleChargeLabel(chargeType, schedulePrice, isCreditEnabled);
};
